import { logger } from '@adornis/base/logging.js';
import {
  subscribeWhitelistAgentEntries,
  subscribeWhitelistTicketEntries,
} from '../api/subscribe-whitelist-entries.js';
import { confirmDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useMemo, useObservable } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { whitelistConfig } from '../config.js';
import { AgentWhitelistEntry } from '../entities/AgentWhitelistEntry.js';
import { WhitelistEntry } from '../entities/WhitelistEntry.js';

const showTicketDialog = stateful(
  (props: { entry?: WhitelistEntry; onSuccess?: (entry: WhitelistEntry) => void; onCancel?: () => void }) => {
    const doc = useMemo(() => [props.entry ?? new WhitelistEntry({})], [props.entry]);
    const form = useForm(doc, {}, [doc]);

    return html`<div class="flex flex-col gap-3 p-4 min-w-[400px]">
      ${TextInput({
        ...form.field('ticketID'),
        placeholder: 'Ticket ID',
      })}
      ${TextInput({
        ...form.field('note'),
        placeholder: 'Note',
      })}
      <div class="divider"></div>
      <div>
        <button
          class="btn btn-primary btn-sm"
          @click=${async () => {
            try {
              await form.validate();
              if (form.document._id) {
                await form.document.save();
              } else {
                await form.document.create();
              }
              props.onSuccess?.(form.document);
            } catch (err) {
              logger.error({ err }, 'Error Saving the white list entry');
            }
          }}
        >
          Save
        </button>
        <button class="btn btn-ghost btn-sm" @click=${() => props.onCancel?.()}>Cancel</button>
      </div>
    </div>`;
  },
);

const showAgentDialog = stateful(
  (props: { entry?: AgentWhitelistEntry; onSuccess?: (entry: AgentWhitelistEntry) => void; onCancel?: () => void }) => {
    const doc = useMemo(() => [props.entry ?? new AgentWhitelistEntry({})], [props.entry]);
    const form = useForm(doc, {}, [doc]);

    return html`<div class="flex flex-col gap-3 p-4 min-w-[400px]">
      ${TextInput({
        ...form.field('agent'),
        placeholder: 'Agent name (assignee)',
      })}
      ${TextInput({
        ...form.field('note'),
        placeholder: 'note',
      })}
      <div class="divider"></div>
      <div>
        <button
          class="btn btn-primary btn-sm"
          @click=${async () => {
            try {
              await form.validate();

              if (form.document._id) {
                await form.document.save();
              } else {
                await form.document.create();
              }
              props.onSuccess?.(form.document);
            } catch (err) {
              logger.error({ err }, 'Error Saving the agent white list entry');
            }
          }}
        >
          Save
        </button>
        <button class="btn btn-ghost btn-sm" @click=${() => props.onCancel?.()}>Cancel</button>
      </div>
    </div>`;
  },
);

export const WhiteListPage = stateful(() => {
  const hasTicketWhitelist = whitelistConfig.get('TICKET_WHITELIST');
  const hasAgentWhitelist = whitelistConfig.get('AGENT_WHITELIST');
  const ticketEntries = useObservable<WhitelistEntry[]>(
    () =>
      subscribeWhitelistTicketEntries()({
        ticketID: 1,
        note: 1,
        _id: 1,
        _class: 1,
      }),
    [],
  );

  const agentEntries = useObservable<AgentWhitelistEntry[]>(
    () =>
      subscribeWhitelistAgentEntries()({
        agent: 1,
        note: 1,
        _id: 1,
        _class: 1,
      }),
    [],
  );

  return html`
    <div class="max-w-6xl mx-auto p-8">
      <h2 class="text-2xl font-bold mb-2">Whitelists</h2>
      <p class="text-sm text-base-content/70 mb-6">
        Tickets are processed if they match <span class="font-semibold">either</span> the Ticket Whitelist
        <span class="opacity-60">(by correlation ID)</span> or the Human Agent Whitelist
        <span class="opacity-60">(by assignee name)</span>.
      </p>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section class="card bg-base-100 shadow-xl rounded-xl border border-neutral/10">
          <div class="card-body">
            <div class="mb-4 flex justify-between items-center">
              <h3 class="card-title text-xl font-bold flex items-center gap-3">
                Ticket Whitelist
                <span class="badge ${hasTicketWhitelist ? 'badge-success' : 'badge-ghost'}">
                  ${hasTicketWhitelist ? 'Enabled' : 'Disabled'}
                </span>
              </h3>
              <button
                class="btn btn-primary btn-sm"
                @click=${async () => {
                  await showDialog({
                    content: resolve => {
                      return showTicketDialog({
                        onSuccess: entry => {
                          resolve(entry);
                        },
                        onCancel: () => {
                          resolve(undefined);
                        },
                      });
                    },
                  });
                }}
              >
                + Add Entry
              </button>
            </div>
            <div class="overflow-x-auto">
              <table class="table w-full">
                <thead>
                  <tr class="bg-base-200">
                    <th>Ticket ID</th>
                    <th>Note</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  ${ticketEntries?.map(
                    entry => html`
                      <tr class="hover">
                        <td>${entry.ticketID}</td>
                        <td>${entry.note}</td>
                        <td class="flex gap-2 justify-center">
                          <button
                            class="btn btn-outline btn-accent btn-xs"
                            @click=${async () => {
                              await showDialog({
                                content: resolve => {
                                  return showTicketDialog({
                                    entry: entry as WhitelistEntry,
                                    onSuccess: e => {
                                      resolve(e);
                                    },
                                    onCancel: () => {
                                      resolve(undefined);
                                    },
                                  });
                                },
                              });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            class="btn btn-outline btn-error btn-xs"
                            @click="${async () => {
                              if (await confirmDialog('Are you sure you want to delete this entry?')) {
                                await entry.remove();
                              }
                            }}"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="card bg-base-100 shadow-xl rounded-xl border border-neutral/10">
          <div class="card-body">
            <div class="mb-4 flex justify-between items-center">
              <h3 class="card-title text-xl font-bold flex items-center gap-3">
                Human Agent Whitelist
                <span class="badge ${hasAgentWhitelist ? 'badge-success' : 'badge-ghost'}">
                  ${hasAgentWhitelist ? 'Enabled' : 'Disabled'}
                </span>
              </h3>
              <button
                class="btn btn-primary btn-sm"
                @click=${async () => {
                  await showDialog({
                    content: resolve => {
                      return showAgentDialog({
                        onSuccess: entry => {
                          resolve(entry);
                        },
                        onCancel: () => {
                          resolve(undefined);
                        },
                      });
                    },
                  });
                }}
              >
                + Add Entry
              </button>
            </div>
            <div class="overflow-x-auto">
              <table class="table w-full">
                <thead>
                  <tr class="bg-base-200">
                    <th>Agent (Assignee)</th>
                    <th>Note</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  ${agentEntries?.map(
                    entry => html`
                      <tr class="hover">
                        <td>${entry.agent}</td>
                        <td>${entry.note}</td>
                        <td class="flex gap-2 justify-center">
                          <button
                            class="btn btn-outline btn-accent btn-xs"
                            @click=${async () => {
                              await showDialog({
                                content: resolve => {
                                  return showAgentDialog({
                                    entry: entry as AgentWhitelistEntry,
                                    onSuccess: e => {
                                      resolve(e);
                                    },
                                    onCancel: () => {
                                      resolve(undefined);
                                    },
                                  });
                                },
                              });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            class="btn btn-outline btn-error btn-xs"
                            @click="${async () => {
                              if (await confirmDialog('Are you sure you want to delete this entry?')) {
                                await entry.remove();
                              }
                            }}"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  `;
});
